import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  EmptystateListColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import { getSearchQuery } from "components/SearchInput/helpers";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import Tooltip from "ds/components/Tooltip";
import useErrorHandle from "hooks/useErrorHandle";
import useCanCreatePluginTemplates from "shared/Plugins/access/useCanCreatePluginTemplates";

import PluginsLayout from "../../components/PluginsLayout";
import { sortRows } from "../../helpers/sortRows";
import { pluginTemplatesTableCells } from "./cells";
import { PLUGIN_TEMPLATES_COLUMN_ID } from "./constants";
import { GET_PLUGIN_TEMPLATES } from "./gql";
import { showPluginTemplateCreateDrawer } from "./PluginTemplateCreateDrawer";
import { PluginTemplatesTableContext } from "./PluginTemplatesTableContext";
import { usePluginTemplatesTableStore } from "./usePluginTemplatesTableStore";

const PluginTemplates = () => {
  const [searchParams] = useSearchParams();
  const { data, loading, error } = useQuery(GET_PLUGIN_TEMPLATES);
  const ErrorContent = useErrorHandle(error);
  const canCreatePluginTemplates = useCanCreatePluginTemplates();

  // TODO: Temporary solution until BE implements sorting and filtering for plugin templates
  const filteredTemplates = useMemo(() => {
    const searchQuery = getSearchQuery(searchParams);

    const filtered =
      data?.pluginTemplates.filter((plugin) =>
        plugin.name.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())
      ) ?? [];

    const sorted = sortRows(searchParams, filtered, PLUGIN_TEMPLATES_COLUMN_ID.NAME);

    return sorted;
  }, [searchParams, data?.pluginTemplates]);

  const allTemplateNames = useMemo(() => {
    return data?.pluginTemplates.map((plugin) => plugin.name) || [];
  }, [data?.pluginTemplates]);

  const createPlugin = useCallback(() => {
    showPluginTemplateCreateDrawer({ takenPluginNames: allTemplateNames });
  }, [allTemplateNames]);

  const noTemplates = !data?.pluginTemplates.length && !filteredTemplates.length && !loading;
  const noSearchResults = !!data?.pluginTemplates.length && filteredTemplates.length === 0;
  const hasTemplateItems = filteredTemplates.length > 0;

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <PluginsLayout
      actions={
        <Tooltip
          on={(tooltipProps) => (
            <Button
              variant="primary"
              onPress={createPlugin}
              disabled={!canCreatePluginTemplates.result}
              {...tooltipProps}
            >
              Create a template
            </Button>
          )}
          active={!canCreatePluginTemplates.result}
        >
          {canCreatePluginTemplates.message}
        </Tooltip>
      }
    >
      {noSearchResults && (
        <EmptyState
          title="No results"
          caption="Don't give up! Check the spelling or try something less specific."
          icon={EmptystateSearchNoResultsColored}
        />
      )}
      {noTemplates && (
        <EmptyState
          title="No templates available"
          caption="You can create templates to quickly set up new plugins."
          icon={EmptystateListColored}
        />
      )}
      {hasTemplateItems && (
        <PluginTemplatesTableContext.Provider value={{ takenPluginNames: allTemplateNames }}>
          <TableSimple
            data={filteredTemplates}
            loading={loading}
            store={usePluginTemplatesTableStore}
            ariaLabel="Plugins table"
            filtersStorageKey="plugin-templates-filters"
            cellPerColumn={pluginTemplatesTableCells}
          />
        </PluginTemplatesTableContext.Provider>
      )}
    </PluginsLayout>
  );
};

export default PluginTemplates;
